/**
 * VisualizationSection 視覺化區段元件
 *
 * 顯示座位規劃結果：
 * - 規劃結果 Canvas
 * - 方案切換
 * - 下載功能
 */

import { Section } from '@/components/layout/Section';
import { Checkbox } from '@/components/shared/FormInput';
import { ProgressBar } from '@/components/shared/ProgressIndicator';
import { PlannerCanvas } from '@/canvas/planner-canvas';
import { preprocessStore } from '@/store/preprocess-store';
import { loadSinglePlan } from '@/store/planner-store';
import { CostDetailModal, type CostLevel } from '@/components/modals/CostDetailModal';
import type { SinglePlan, ApiRoomInfo, CostBreakdown } from '@/types/api';

// ========================================================================
// 型別定義
// ========================================================================

export interface VisualizationSectionOptions {
  /** 下載方案回呼 */
  onDownload?: (planIndex: number) => void;
  /** 下載全部方案回呼 */
  onDownloadAll?: () => void;
}

// ========================================================================
// VisualizationSection 類別
// ========================================================================

export class VisualizationSection {
  private section: Section;
  private canvas: PlannerCanvas | null = null;
  private canvasElement: HTMLCanvasElement | null = null;
  private options: VisualizationSectionOptions;
  private unsubscribe: (() => void) | null = null;

  // 狀態
  private plans: SinglePlan[] = [];
  private _roomInfo: ApiRoomInfo | null = null;
  private currentPlanIndex: number = 0;

  // UI 元素
  private loadingContainer: HTMLDivElement | null = null;
  private resultContainer: HTMLDivElement | null = null;
  private progressBar: ProgressBar | null = null;

  // 顯示選項
  private showDoorClearance: Checkbox;
  private showCorridors: Checkbox;
  private showDimensions: Checkbox;
  private showPillars: Checkbox;
  private showRoomOutline: Checkbox;
  private showOfficeAreas: Checkbox;

  // 費用明細 Modal
  private costDetailModal: CostDetailModal;

  constructor(options: VisualizationSectionOptions = {}) {
    this.options = options;

    // 建立區段
    this.section = new Section({
      title: '步驟 3：座位規劃結果',
      description: '查看和編輯座位配置',
      stepNumber: 3,
      visible: false,
    });

    // 顯示選項（與 HTML 版本一致，預設全部不顯示）
    this.showRoomOutline = new Checkbox('顯示房間輪廓', { checked: false });
    this.showDoorClearance = new Checkbox('顯示門淨空區域', { checked: false });
    this.showCorridors = new Checkbox('顯示走道', { checked: false });
    this.showDimensions = new Checkbox('顯示尺寸', { checked: false });
    this.showPillars = new Checkbox('顯示柱子', { checked: false });
    this.showOfficeAreas = new Checkbox('顯示辦公座位區', { checked: false });

    // 建立費用明細 Modal
    this.costDetailModal = new CostDetailModal();

    this.assembleUI();
    this.subscribeToStore();
  }

  // ========================================================================
  // 公開方法
  // ========================================================================

  getElement(): HTMLElement {
    return this.section.getElement();
  }

  getRoomInfo(): ApiRoomInfo | null {
    return this._roomInfo;
  }

  /**
   * 取得當前 Canvas 截圖
   */
  async getCanvasBlob(): Promise<Blob | null> {
    return this.canvas?.exportAsBlob('png') ?? null;
  }

  /**
   * 取得當前方案索引
   */
  getCurrentPlanIndex(): number {
    return this.currentPlanIndex;
  }

  /**
   * 取得方案總數
   */
  getTotalPlans(): number {
    return this.plans.length;
  }

  show(): void {
    this.section.show();
  }

  hide(): void {
    this.section.hide();
  }

  /**
   * 設定規劃結果
   */
  setPlans(plans: SinglePlan[], roomInfo?: ApiRoomInfo): void {
    this.plans = plans;
    this._roomInfo = roomInfo ?? null;
    this.currentPlanIndex = 0;
    this.showResults();

    if (plans.length > 0) {
      this.renderPlan(plans[0]!);
    }

    // 更新方案資訊顯示（例如：方案 1/16）
    this.updatePlanInfo();
  }

  /**
   * 顯示載入狀態
   */
  showLoading(message?: string): void {
    this.section.show();

    if (this.loadingContainer) {
      this.loadingContainer.classList.remove('hidden');
    }
    if (this.resultContainer) {
      this.resultContainer.classList.add('hidden');
    }

    // 更新載入訊息
    const messageEl = this.loadingContainer?.querySelector('#loadingMessage');
    if (messageEl && message) {
      messageEl.textContent = message;
    }
  }

  /**
   * 更新進度
   */
  updateProgress(value: number, message?: string): void {
    this.progressBar?.setValue(value);

    if (message) {
      const messageEl = this.loadingContainer?.querySelector('#loadingMessage');
      if (messageEl) {
        messageEl.textContent = message;
      }
    }
  }

  /**
   * 隱藏載入狀態，顯示結果
   */
  showResults(): void {
    if (this.loadingContainer) {
      this.loadingContainer.classList.add('hidden');
    }
    if (this.resultContainer) {
      this.resultContainer.classList.remove('hidden');
    }
  }

  mount(container: HTMLElement): void {
    this.section.mount(container);
  }

  /**
   * 取得 Canvas 元素（用於風格渲染）
   */
  getCanvas(): HTMLCanvasElement | null {
    return this.canvasElement;
  }

  destroy(): void {
    this.unsubscribe?.();
    this.canvas?.destroy();
    this.costDetailModal.destroy();
    this.section.destroy();
  }

  // ========================================================================
  // 私有方法 - UI 建構
  // ========================================================================

  private assembleUI(): void {
    const content = this.section.getContent();

    content.innerHTML = `
      <!-- 載入中 -->
      <div id="loadingContainer" class="hidden py-12">
        <div class="flex flex-col items-center">
          <div class="inline-block h-12 w-12 animate-spin rounded-full border-4 border-solid border-primary-600 border-r-transparent mb-4"></div>
          <p id="loadingMessage" class="text-gray-600 mb-4">正在規劃座位配置...</p>
          <div id="progressBarContainer" class="w-64"></div>
        </div>
      </div>

      <!-- 結果區域 -->
      <div id="resultContainer" class="hidden space-y-4">
        <!-- 顯示選項 -->
        <div class="flex flex-wrap items-center gap-4" id="displayOptionsContainer">
          <span class="text-sm font-medium text-gray-700">顯示選項：</span>
        </div>

        <!-- Canvas -->
        <div class="relative border border-gray-200 rounded-lg overflow-hidden bg-gray-100">
          <canvas id="visualizationCanvas" class="w-full"></canvas>
        </div>

        <!-- 方案切換 -->
        <div class="flex items-center justify-between">
          <div class="flex items-center gap-3" id="planSwitcher">
            <button type="button" id="prevPlanBtn" class="p-2 rounded-lg border border-gray-300 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed">
              <svg class="w-5 h-5 text-gray-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7"/>
              </svg>
            </button>
            <span id="planInfo" class="text-sm text-gray-600">方案 1 / 1</span>
            <button type="button" id="nextPlanBtn" class="p-2 rounded-lg border border-gray-300 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed">
              <svg class="w-5 h-5 text-gray-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7"/>
              </svg>
            </button>
          </div>

          <div class="flex gap-2">
            <button type="button" id="downloadBtn" class="px-4 py-2 text-sm font-medium text-green-700 bg-green-100 hover:bg-green-200 rounded-lg transition-colors">
              下載此方案
            </button>
            <button type="button" id="downloadAllBtn" class="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors">
              下載全部方案
            </button>
          </div>
        </div>

        <!-- 規劃摘要 -->
        <div id="planSummary" class="p-4 bg-gray-50 rounded-lg">
          <h4 class="text-sm font-medium text-gray-900 mb-2">規劃摘要</h4>
          <div class="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
            <div>
              <span class="text-gray-500">辦公桌數量</span>
              <p id="summaryDesks" class="font-semibold text-gray-900">-</p>
            </div>
            <div>
              <span class="text-gray-500">主管室</span>
              <p id="summarySupervisor" class="font-semibold text-gray-900">-</p>
            </div>
            <div>
              <span class="text-gray-500">會議室</span>
              <p id="summaryMeetingRooms" class="font-semibold text-gray-900">-</p>
            </div>
          </div>
        </div>

        <!-- 費用估計區塊 -->
        <div id="costEstimationSection" class="hidden p-4 bg-white rounded-lg border border-gray-200">
          <h4 class="text-lg font-semibold text-gray-900 mb-4">費用估計</h4>

          <!-- 規劃概要 -->
          <div class="mb-4 p-3 bg-gray-50 rounded">
            <h5 class="text-sm font-medium text-gray-700 mb-2">規劃概要</h5>
            <div class="grid grid-cols-2 md:grid-cols-6 gap-2 text-sm">
              <div>房間坪數：<span id="costRoomPing" class="font-semibold">-</span></div>
              <div>辦公桌組數：<span id="costDesks" class="font-semibold">-</span></div>
              <div>主管室：<span id="costSupervisor" class="font-semibold">-</span></div>
              <div>大會議室：<span id="costLargeMeeting" class="font-semibold">-</span></div>
              <div>中會議室：<span id="costMediumMeeting" class="font-semibold">-</span></div>
              <div>小會議室：<span id="costSmallMeeting" class="font-semibold">-</span></div>
            </div>
          </div>

          <!-- 各等級費用估計表格 -->
          <div>
            <h5 class="text-sm font-medium text-gray-700 mb-2">各等級費用估計</h5>
            <table class="w-full text-center border-collapse text-sm">
              <thead class="bg-gray-100">
                <tr>
                  <th class="py-2 px-4 border">簡易</th>
                  <th class="py-2 px-4 border">標準</th>
                  <th class="py-2 px-4 border">高級</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td class="py-3 px-4 text-base font-bold text-primary-600 border" id="costSimpleTotal">-</td>
                  <td class="py-3 px-4 text-base font-bold text-primary-600 border" id="costStandardTotal">-</td>
                  <td class="py-3 px-4 text-base font-bold text-primary-600 border" id="costPremiumTotal">-</td>
                </tr>
                <tr>
                  <td class="py-2 px-4 border">
                    <button class="costDetailBtn px-3 py-1 text-sm bg-primary-600 text-white rounded hover:bg-primary-700 transition-colors" data-level="simple">看明細</button>
                  </td>
                  <td class="py-2 px-4 border">
                    <button class="costDetailBtn px-3 py-1 text-sm bg-primary-600 text-white rounded hover:bg-primary-700 transition-colors" data-level="standard">看明細</button>
                  </td>
                  <td class="py-2 px-4 border">
                    <button class="costDetailBtn px-3 py-1 text-sm bg-primary-600 text-white rounded hover:bg-primary-700 transition-colors" data-level="premium">看明細</button>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    `;

    // 初始化元素參考
    this.loadingContainer = content.querySelector('#loadingContainer');
    this.resultContainer = content.querySelector('#resultContainer');

    // 進度條
    this.progressBar = new ProgressBar({ value: 0, showLabel: true });
    const progressContainer = content.querySelector('#progressBarContainer');
    if (progressContainer) {
      this.progressBar.mount(progressContainer as HTMLElement);
    }

    // 顯示選項
    const optionsContainer = content.querySelector('#displayOptionsContainer');
    if (optionsContainer) {
      this.showRoomOutline.mount(optionsContainer as HTMLElement);
      this.showDoorClearance.mount(optionsContainer as HTMLElement);
      this.showCorridors.mount(optionsContainer as HTMLElement);
      this.showDimensions.mount(optionsContainer as HTMLElement);
      this.showPillars.mount(optionsContainer as HTMLElement);
      this.showOfficeAreas.mount(optionsContainer as HTMLElement);
    }

    // 初始化 Canvas
    this.canvasElement = content.querySelector('#visualizationCanvas');
    if (this.canvasElement) {
      this.canvas = new PlannerCanvas({
        canvas: this.canvasElement,
      });
      this.canvas.initialize();
    }

    this.bindEvents(content);
  }

  private bindEvents(content: HTMLElement): void {
    const prevBtn = content.querySelector('#prevPlanBtn');
    const nextBtn = content.querySelector('#nextPlanBtn');
    const downloadBtn = content.querySelector('#downloadBtn');
    const downloadAllBtn = content.querySelector('#downloadAllBtn');

    prevBtn?.addEventListener('click', () => this.showPreviousPlan());
    nextBtn?.addEventListener('click', () => this.showNextPlan());
    downloadBtn?.addEventListener('click', () => this.handleDownload());
    downloadAllBtn?.addEventListener('click', () => this.handleDownloadAll());

    // 費用明細按鈕事件
    const costDetailBtns = content.querySelectorAll('.costDetailBtn');
    costDetailBtns.forEach((btn) => {
      btn.addEventListener('click', (e) => {
        const target = e.currentTarget as HTMLButtonElement;
        const level = target.dataset.level as CostLevel;
        this.handleShowCostDetail(level);
      });
    });

    // 顯示選項變更 - 呼叫 canvas 的 setter 方法更新顯示狀態
    this.showRoomOutline.getElement().addEventListener('change', () => {
      this.canvas?.setShowRoomOutline(this.showRoomOutline.isChecked());
    });
    this.showDoorClearance.getElement().addEventListener('change', () => {
      this.canvas?.setShowClearanceZones(this.showDoorClearance.isChecked());
    });
    this.showCorridors.getElement().addEventListener('change', () => {
      this.canvas?.setShowCorridors(this.showCorridors.isChecked());
    });
    this.showDimensions.getElement().addEventListener('change', () => {
      this.canvas?.setShowDimensions(this.showDimensions.isChecked());
    });
    this.showPillars.getElement().addEventListener('change', () => {
      this.canvas?.setShowPillars(this.showPillars.isChecked());
    });
    this.showOfficeAreas.getElement().addEventListener('change', () => {
      this.canvas?.setShowOfficeAreas(this.showOfficeAreas.isChecked());
    });
  }

  // ========================================================================
  // 私有方法 - 方案管理
  // ========================================================================

  private showPreviousPlan(): void {
    if (this.currentPlanIndex > 0) {
      this.currentPlanIndex--;
      this.renderCurrentPlan();
    }
  }

  private showNextPlan(): void {
    if (this.currentPlanIndex < this.plans.length - 1) {
      this.currentPlanIndex++;
      this.renderCurrentPlan();
    }
  }

  private renderCurrentPlan(): void {
    const plan = this.plans[this.currentPlanIndex];
    if (plan) {
      this.renderPlan(plan);
    }
    this.updatePlanInfo();
  }

  private renderPlan(plan: SinglePlan): void {
    // 載入方案資料到 store（家具、走道、淨空區域等）
    loadSinglePlan(plan);

    // 從 preprocess store 載入預處理後的圖片（使用者編輯後），若無則回退到原始圖片
    const preprocessState = preprocessStore.getState();
    const imageDataUrl = preprocessState.processedDataUrl || preprocessState.originalDataUrl;

    if (imageDataUrl && this.canvas) {
      const img = new Image();
      img.onload = () => {
        this.canvas?.setImage(img);
        this.canvas?.render();
      };
      img.src = imageDataUrl;
    }

    // 更新摘要
    this.updateSummary(plan);
  }

  private updatePlanInfo(): void {
    const infoEl = this.section.getElement().querySelector('#planInfo');
    const prevBtn = this.section.getElement().querySelector('#prevPlanBtn') as HTMLButtonElement;
    const nextBtn = this.section.getElement().querySelector('#nextPlanBtn') as HTMLButtonElement;

    if (infoEl) {
      infoEl.textContent = `方案 ${this.currentPlanIndex + 1} / ${this.plans.length}`;
    }

    if (prevBtn) {
      prevBtn.disabled = this.currentPlanIndex === 0;
    }

    if (nextBtn) {
      nextBtn.disabled = this.currentPlanIndex === this.plans.length - 1;
    }
  }

  private updateSummary(plan: SinglePlan): void {
    const desksEl = this.section.getElement().querySelector('#summaryDesks');
    const supervisorEl = this.section.getElement().querySelector('#summarySupervisor');
    const meetingEl = this.section.getElement().querySelector('#summaryMeetingRooms');

    // 從 furniture_plan.summary 取得數據
    const summary = plan.furniture_plan?.summary;

    if (desksEl) {
      desksEl.textContent = `${summary?.desks_placed ?? '-'} 個`;
    }

    if (supervisorEl) {
      supervisorEl.textContent = `${summary?.supervisor_rooms_placed ?? 0} 間`;
    }

    if (meetingEl) {
      // 計算總會議室數量
      const meetingRoomCount = (summary?.large_meeting_rooms_placed ?? 0) +
                               (summary?.medium_meeting_rooms_placed ?? 0) +
                               (summary?.small_meeting_rooms_placed ?? 0);
      meetingEl.textContent = `${meetingRoomCount} 間`;
    }

    // 更新費用估計區塊
    this.updateCostEstimation(plan.cost_breakdown);
  }

  private updateCostEstimation(costData: CostBreakdown | undefined): void {
    const costSection = this.section.getElement().querySelector('#costEstimationSection');

    if (!costData || !costSection) {
      costSection?.classList.add('hidden');
      return;
    }

    // 顯示費用估計區塊
    costSection.classList.remove('hidden');

    // 更新規劃概要
    const roomPingEl = costSection.querySelector('#costRoomPing');
    const desksEl = costSection.querySelector('#costDesks');
    const supervisorEl = costSection.querySelector('#costSupervisor');
    const largeMeetingEl = costSection.querySelector('#costLargeMeeting');
    const mediumMeetingEl = costSection.querySelector('#costMediumMeeting');
    const smallMeetingEl = costSection.querySelector('#costSmallMeeting');

    if (roomPingEl) roomPingEl.textContent = costData.room_area_ping.toString();
    if (desksEl) desksEl.textContent = costData.desks_placed.toString();
    if (supervisorEl) supervisorEl.textContent = costData.supervisor_rooms_placed.toString();
    if (largeMeetingEl) largeMeetingEl.textContent = costData.large_meeting_rooms_placed.toString();
    if (mediumMeetingEl) mediumMeetingEl.textContent = costData.medium_meeting_rooms_placed.toString();
    if (smallMeetingEl) smallMeetingEl.textContent = costData.small_meeting_rooms_placed.toString();

    // 更新三個等級的總價
    const simpleTotalEl = costSection.querySelector('#costSimpleTotal');
    const standardTotalEl = costSection.querySelector('#costStandardTotal');
    const premiumTotalEl = costSection.querySelector('#costPremiumTotal');

    if (simpleTotalEl) simpleTotalEl.textContent = this.formatCurrency(costData.simple.total);
    if (standardTotalEl) standardTotalEl.textContent = this.formatCurrency(costData.standard.total);
    if (premiumTotalEl) premiumTotalEl.textContent = this.formatCurrency(costData.premium.total);
  }

  private handleShowCostDetail(level: CostLevel): void {
    const plan = this.plans[this.currentPlanIndex];
    if (plan?.cost_breakdown) {
      this.costDetailModal.open(plan.cost_breakdown, level);
    }
  }

  private formatCurrency(amount: number): string {
    return new Intl.NumberFormat('zh-TW', {
      style: 'currency',
      currency: 'TWD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  }

  private handleDownload(): void {
    this.options.onDownload?.(this.currentPlanIndex);
  }

  private handleDownloadAll(): void {
    this.options.onDownloadAll?.();
  }

  private subscribeToStore(): void {
    // 可以訂閱 store 來同步狀態
  }
}
